import React, { SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { Table, Button, Skeleton } from 'components'
import { THead, TBody, Row, ActionEventTable } from 'components/Table'
import {
  COLUMNS_SELECT_BRANCH,
  COLUMNS_SELECT_BRANCH_LOADING,
} from 'features/Ticket'
import { setDescriptionTwoLine, setDescriptionFullContent } from 'helpers/table'
import { routes } from 'helpers/routes'

import type { ISorting } from 'types'
import type { ITicketSelectBranchListProps } from 'features/Ticket/TicketSelectBranch'
import type { ITicketSelectBranchList } from './model'

function TicketSelectBranchList(
  props: ITicketSelectBranchListProps,
): JSX.Element {
  const { t } = useTranslation(['common', 'ticket', 'branch'])
  const history = useHistory()

  const { isLoading, isError, branches, setSearchParams, searchParams } = props

  const base = 'f-ticket-select-branch'

  const handleSortChange = (sort: ISorting): void => {
    setSearchParams({ _sort: sort.column, _order: sort.order, _page: 1 })
  }

  const handleClickRow =
    (branch: ITicketSelectBranchList) => (e: SyntheticEvent) => {
      e.preventDefault()
      history.push({
        pathname: `${routes.ticket.path}/branch/${branch.id}/new`,
        state: branch,
      })
    }

  const renderContent = (branches: ITicketSelectBranchList[]) => {
    const SkeletonListOdd: Record<string, React.ReactNode> = {
      no: <Skeleton.whiteSkeleton width='256px' />,
      companyName: <Skeleton.whiteSkeleton width='207px' />,
      phone: <Skeleton.whiteSkeleton width='98px' />,
      selectBranch: <Skeleton.whiteSkeleton width='100px' height='36px' />,
    }

    const SkeletonListEven: Record<string, React.ReactNode> = {
      no: <Skeleton.whiteSkeleton width='368px' />,
      companyName: <Skeleton.whiteSkeleton width='298px' />,
      phone: <Skeleton.whiteSkeleton width='98px' />,
      selectBranch: <Skeleton.whiteSkeleton width='100px' height='36px' />,
    }

    const list: Record<string, React.ReactNode>[] = branches.map(
      (branch: ITicketSelectBranchList) => {
        return {
          no: branch.no,
          name: setDescriptionTwoLine(branch.branchName),
          companyName: setDescriptionTwoLine(branch.companyName),
          phone: setDescriptionFullContent(branch.phone),
          selectBranch: (
            <Button className={`${base}__select-button`} variants='outline'>
              {t('common:select.button')}
            </Button>
          ),
        }
      },
    )

    if (isLoading) {
      return (
        <Skeleton.SkeletonList
          columns={COLUMNS_SELECT_BRANCH_LOADING}
          rowOdd={SkeletonListOdd}
          rowEven={SkeletonListEven}
        />
      )
    }

    if (isEmpty(branches) || isError) {
      return (
        <ActionEventTable
          colSpan={COLUMNS_SELECT_BRANCH_LOADING.length}
          customNotFoundText={t('branch:notFound.label')}
          isNotFound
        />
      )
    }

    return (
      <TBody>
        {list.map((data, index) => (
          <Row
            data-testid={`ticket-select-branch-row-${branches[index].id}`}
            key={index}
            columns={COLUMNS_SELECT_BRANCH}
            data={data}
            onClick={handleClickRow(branches[index])}
            position='left'
          />
        ))}
      </TBody>
    )
  }

  return (
    <div className={`${base}__list`}>
      <span className={`${base}__title`}>
        {t('ticket:customerDetail.column')}
      </span>

      <div className={`${base}__list--body`}>
        <Table>
          <THead
            columns={COLUMNS_SELECT_BRANCH}
            onSortChange={handleSortChange}
            sorting={{
              column: searchParams._sort as string,
              order: searchParams._order || 'DESC',
            }}
            position='left'
          />
          {renderContent(branches)}
        </Table>
      </div>
    </div>
  )
}

export default TicketSelectBranchList
